// 14–18 (learner) icon pack — draws from Phosphor (react-icons/pi).
//
// Phosphor is friendly, rounded and modern — age-appropriate for older teens and
// clearly distinct from the admin tool's bespoke line icons. Normal vs selected
// states use Phosphor's Regular vs Fill weights (e.g. PiHouse / PiHouseFill).
//
// Named imports from "react-icons/pi" are tree-shaken by the CRA/webpack build,
// so only the glyphs referenced below are bundled — not the whole pack.
//
// A future 10–14 pack maps the SAME canonical IconNames to a more playful set.

import { IconType } from "react-icons";
import {
  PiHouse,
  PiHouseFill,
  PiFolder,
  PiFolderFill,
  PiChartBar,
  PiChartBarFill,
  PiMegaphone,
  PiMegaphoneFill,
  PiCalendarBlank,
  PiCalendarBlankFill,
  PiTarget,
  PiGear,
  PiSignOut,
  PiDownloadSimple,
  PiArrowsClockwise,
  PiUploadSimple,
  PiInfo,
  PiWarning,
  PiCellSignalHigh,
  PiX,
  PiCheckCircle,
  PiXCircle,
  PiCheckSquare,
  PiSquare,
  PiMinusSquare,
  PiXSquare,
  PiDotsThree,
  PiFile,
  PiPencilSimple,
  PiTrash,
  PiImage,
  PiSpeakerHigh,
  PiVideoCamera,
  PiTextT,
  PiPaperPlaneRight,
  PiUserSwitch,
  PiArrowUp,
  PiArrowDown,
  PiArrowLeft,
  PiArrowRight,
  PiCaretUp,
  PiCaretDown,
  PiCaretLeft,
  PiCaretRight,
  PiArrowUpRight,
  PiPlus,
  PiPlay,
  PiPause,
  PiClock,
  PiSparkle,
  PiUmbrella,
  PiBuildings,
  PiBank,
  PiTreeStructure,
  PiGraduationCap,
  PiSun,
  PiExam,
  PiClipboardText,
  PiClipboardTextFill,
  PiBookOpen,
  PiBookOpenFill,
  PiPath,
  PiPathFill,
  PiListChecks,
  PiUser,
  PiStar,
  PiStarFill,
  PiCircle,
  PiCircleFill,
  PiMapTrifold,
  PiMagnifyingGlass,
  PiTray,
  PiConfetti,
  PiUsersThree,
  PiSword,
  PiLockSimple,
  PiEraser,
  PiLineSegment,
  PiRectangle,
  PiScribbleLoop,
  PiCursor,
  PiCrownFill,
  PiCoinFill,
  PiLightningFill,
  PiBackpack,
  PiBackpackFill,
  PiStorefront,
  PiStorefrontFill,
  PiTrophy,
  PiTrophyFill,
  PiPlayCircle,
  PiPlayCircleFill,
  PiCamera,
  PiCameraFill,
  PiUserCircle,
  PiUserCircleFill,
  PiGiftFill,
  PiTranslateFill,
  PiMicrophoneFill,
  PiSpeakerHighFill,
} from "react-icons/pi";
import { IconName } from "../iconNames";

export const TEEN_PACK: Record<IconName, IconType> = {
  // navigation
  house: PiHouse,
  houseFill: PiHouseFill,
  folder: PiFolder,
  folderFill: PiFolderFill,
  chartBar: PiChartBar,
  chartBarFill: PiChartBarFill,
  megaphone: PiMegaphone,
  megaphoneFill: PiMegaphoneFill,
  calendar: PiCalendarBlank,
  calendarFill: PiCalendarBlankFill,
  target: PiTarget,
  gear: PiGear,
  signOut: PiSignOut,
  // actions
  download: PiDownloadSimple,
  refresh: PiArrowsClockwise,
  uploadDevice: PiUploadSimple,
  info: PiInfo,
  warning: PiWarning,
  signal: PiCellSignalHigh,
  close: PiX,
  checkCircle: PiCheckCircle,
  xCircle: PiXCircle,
  checkboxFilled: PiCheckSquare,
  checkboxEmpty: PiSquare,
  checkboxPartial: PiMinusSquare,
  checkboxCross: PiXSquare,
  more: PiDotsThree,
  fileBlank: PiFile,
  pencil: PiPencilSimple,
  trash: PiTrash,
  image: PiImage,
  audio: PiSpeakerHigh,
  video: PiVideoCamera,
  text: PiTextT,
  send: PiPaperPlaneRight,
  userSwitch: PiUserSwitch,
  // arrows / chevrons
  arrowUp: PiArrowUp,
  arrowDown: PiArrowDown,
  arrowLeft: PiArrowLeft,
  arrowRight: PiArrowRight,
  chevronUp: PiCaretUp,
  chevronDown: PiCaretDown,
  chevronLeft: PiCaretLeft,
  chevronRight: PiCaretRight,
  arrowUpRight: PiArrowUpRight,
  plus: PiPlus,
  // media
  play: PiPlay,
  pause: PiPause,
  clock: PiClock,
  sparkle: PiSparkle,
  // domain / org hierarchy
  umbrella: PiUmbrella,
  buildings: PiBuildings,
  bank: PiBank,
  treeStructure: PiTreeStructure,
  graduationCap: PiGraduationCap,
  sun: PiSun,
  exam: PiExam,
  clipboardText: PiClipboardText,
  clipboardTextFill: PiClipboardTextFill,
  bookOpen: PiBookOpen,
  bookOpenFill: PiBookOpenFill,
  path: PiPath,
  pathFill: PiPathFill,
  listChecks: PiListChecks,
  user: PiUser,
  // status dots / stars
  star: PiStar,
  starFill: PiStarFill,
  circle: PiCircle,
  circleFill: PiCircleFill,
  // empty / gate states (the <EmptyState> medallion glyphs) — Regular weight, so
  // the medallion reads as one outline glyph in the brand tint
  map: PiMapTrifold,
  magnifyingGlass: PiMagnifyingGlass,
  tray: PiTray,
  confetti: PiConfetti,
  users: PiUsersThree,
  sword: PiSword,
  lock: PiLockSimple,
  // support-assignment / drawing toolbar
  eraser: PiEraser,
  lineSegment: PiLineSegment,
  rectangle: PiRectangle,
  scribble: PiScribbleLoop,
  cursor: PiCursor,
  // colored gamification / feature illustrations (Fill weight; painted a fixed
  // identity color via the alias defaultColor in iconNames.ts)
  crownFill: PiCrownFill,
  coin: PiCoinFill,
  lightning: PiLightningFill,
  backpack: PiBackpackFill,
  storefront: PiStorefrontFill,
  trophy: PiTrophyFill,
  playCircle: PiPlayCircleFill,
  camera: PiCameraFill,
  userCircle: PiUserCircleFill,
  // outline (Regular) variants — used at rest in the nav so every nav item is
  // outline-when-inactive / Fill-when-active (matches house/houseFill, etc.)
  storefrontLine: PiStorefront,
  trophyLine: PiTrophy,
  playCircleLine: PiPlayCircle,
  cameraLine: PiCamera,
  userCircleLine: PiUserCircle,
  backpackLine: PiBackpack,
  gift: PiGiftFill,
  translate: PiTranslateFill,
  microphone: PiMicrophoneFill,
  speakerFill: PiSpeakerHighFill,
};
